import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom, Subject } from "rxjs";
import { IDashboardModel, isiFirmTaxDashboard } from "../../views-interfaces/dashboard-model.interface";
import { ITaxReturnStatus } from "../../views-interfaces/tax-return-status-model.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { IfirmTaxReturnStatusesService } from "../ifirm-tax-return-status-service/ifirm-tax-return-statuses.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import {
    ITaxReturnSingleStatusResponse,
    ITaxReturnStatusDeleteResponse,
    ITaxReturnStatusListResponse,
} from "./tax-return-status-model-httpResponseRequest.interface";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { JobsAndBillingStatusesService } from "../jobs-and-billing-statuses-service/jobs-and-billing-statuses.service";

@Injectable()
export class TaxReturnStatusesService {
    private appSettingsService = inject(AppSettingsService);
    private ifirmTaxReturnStatusesService = inject(IfirmTaxReturnStatusesService);
    private jobsAndBillingStatusesService = inject(JobsAndBillingStatusesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private http = inject(HttpClient);

    private TaxReturnStatuses: ITaxReturnStatus[] = [];
    private dashboardId: string;
    private lastDashboardId: string;
    taxReturnStatusesLoadedEmitter = new Subject();
    taxReturnStatusesLoadedEvent$ = this.taxReturnStatusesLoadedEmitter.asObservable();
    taxReturnStatusesLoaded = false;
    private taxReturnStatusesCache: Map<string, Promise<ITaxReturnStatus[]>> = new Map<string, Promise<ITaxReturnStatus[]>>();
    currentDashboard: IDashboardModel;

    constructor() {
        this.authService.fetchedUserInfoEvent$.subscribe(async () => {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
            this.dashboardId = this.dashboardsService.getCurrentDashboardId();
        });

        this.dashboardsService.dashboardChangeEvent$.subscribe(async () => {
            this.taxReturnStatusesLoaded = false;
            this.dashboardId = undefined;
            this.currentDashboard = undefined;
        });

        this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            this.dashboardId = this.dashboardsService.getCurrentDashboardId();
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
            if (this.taxReturnStatusesLoaded) {
                this.refresh();
            }
        });
    }

    refresh() {
        this.taxReturnStatusesLoaded = false;
        this.TaxReturnStatuses = [];
        this.taxReturnStatusesCache = new Map<string, Promise<ITaxReturnStatus[]>>();
    }

    async loadTaxReturnStatuses() {
        if (!this.currentDashboard) {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        }
        if (isiFirmTaxDashboard(this.currentDashboard)) {
            try {
                this.TaxReturnStatuses = await this.ifirmTaxReturnStatusesService.getReturnStatuses(this.currentDashboard.ApplicationType);
                this.taxReturnStatusesLoaded = true;
                return this.TaxReturnStatuses;
            } catch (error) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        } else if (isJobsBilling(this.currentDashboard.ApplicationType)) {
            try {
                this.TaxReturnStatuses = await this.jobsAndBillingStatusesService.getJobStatuses();
                this.taxReturnStatusesLoaded = true;
                return this.TaxReturnStatuses;
            } catch (error) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        } else {
            try {
                const result = await firstValueFrom(
                    this.http.get<ITaxReturnStatusListResponse>(this.getUrl(), {
                        headers: new HttpHeaders().set("Content-Type", "application/json"),
                    }),
                );
                if (result.Code == 200) {
                    this.TaxReturnStatuses = result.TaxReturnStatuses;
                    this.TaxReturnStatuses.forEach((status) => (status.Active = true));
                }
                return result.TaxReturnStatuses;
            } catch (error) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        }
    }

    async getTaxReturnStatusById(id: string) {
        return (await this.getTaxReturnStatusesList()).find((it) => it.Id === id);
    }

    async getTaxReturnStatusesList() {
        this.dashboardId = await this.dashboardsService.getSafeCurrentDashboardId();
        if (!this.taxReturnStatusesCache.get(this.dashboardId)) {
            this.taxReturnStatusesCache.set(this.dashboardId, this.loadTaxReturnStatuses());
        }
        if (this.lastDashboardId !== this.dashboardId) {
            this.taxReturnStatusesCache.get(this.dashboardId).then(() => {
                this.taxReturnStatusesLoaded = true;
                this.taxReturnStatusesLoadedEmitter.next(true);
            });
            this.lastDashboardId = this.dashboardId;
        }
        return this.taxReturnStatusesCache.get(this.dashboardId) ?? [];
    }

    addStatus(statusData: ITaxReturnStatus) {
        return this.http.post<ITaxReturnSingleStatusResponse>(this.getUrl(), statusData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    updateStatus(statusId: string, statusData: ITaxReturnStatus) {
        return this.http.put<ITaxReturnSingleStatusResponse>(this.getUrl() + statusId, statusData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    deleteStatus(statusId: string) {
        return this.http.delete<ITaxReturnStatusDeleteResponse>(this.getUrl() + statusId);
    }

    private getUrl(path = "tax-return-status/") {
        return (
            this.appSettingsService.appSettings.TaxReturnStatusesService_URL +
            this.appSettingsService.appSettings.TaxReturnStatusesService_Version +
            path +
            this.dashboardId +
            "/"
        );
    }
}
